import { useTranslation } from 'react-i18next';
import SkillBadge from './SkillBadge';
import type { EmployeePersonaFit } from '../types';

interface PersonaCardProps {
  personaName: string;
  description: string;
  requiredSkills: string[];
  reason?: string;
  isCustom?: boolean;
  employees?: EmployeePersonaFit[];
  /** When true, show the employee list expanded */
  expanded?: boolean;
  onToggle?: () => void;
}

function scoreTier(score: number): { label: string; color: string } {
  if (score >= 80) return { label: 'Excellent', color: 'text-green-700 bg-green-100' };
  if (score >= 60) return { label: 'Good', color: 'text-blue-700 bg-blue-100' };
  if (score >= 40) return { label: 'Partial', color: 'text-amber-700 bg-amber-100' };
  return { label: 'Low', color: 'text-red-700 bg-red-100' };
}

export default function PersonaCard({
  personaName,
  description,
  requiredSkills,
  reason,
  isCustom = false,
  employees,
  expanded = false,
  onToggle,
}: PersonaCardProps) {
  const { t } = useTranslation();

  return (
    <div className="card rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{personaName}</h3>
            {isCustom && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-purple-300">
                {t('analytics.customPersona', 'Custom')}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
          {reason && (
            <p className="mt-2 text-sm text-gray-600 italic">{reason}</p>
          )}
        </div>
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="ml-2 rounded-md p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-expanded={expanded}
            aria-label={expanded
              ? t('analytics.collapseEmployees', 'Collapse employees')
              : t('analytics.expandEmployees', 'Expand employees')}
          >
            <span aria-hidden="true" className="text-lg">{expanded ? '▾' : '▸'}</span>
          </button>
        )}
      </div>

      {/* Required skills */}
      <div className="mt-3" role="list" aria-label={t('analytics.requiredSkills', 'Required skills')}>
        <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
          {t('analytics.requiredSkills', 'Required Skills')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(requiredSkills ?? []).map((skill) => (
            <SkillBadge key={skill} skill={skill} matched optional={false} />
          ))}
        </div>
      </div>

      {/* Employee fits */}
      {expanded && employees && employees.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
            {t('analytics.matchingEmployees', 'Matching Employees')}
          </p>
          <div className="space-y-2">
            {employees.map((emp) => {
              const pct = Math.round(emp.fit_score * 100);
              const tier = scoreTier(pct);
              return (
                <div key={emp.employee_id} className="rounded-lg bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900">{emp.employee_name}</span>
                      {emp.department && (
                        <span className="ml-2 text-xs text-gray-500">{emp.department}</span>
                      )}
                    </div>
                    <span className={['rounded-full px-2 py-0.5 text-xs font-semibold', tier.color].join(' ')}>
                      {pct}% &middot; {tier.label}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(emp.matched_skills ?? []).map((s) => (
                      <SkillBadge key={s} skill={s} matched />
                    ))}
                    {(emp.missing_skills ?? []).map((s) => (
                      <SkillBadge key={s} skill={s} matched={false} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
